/**
 * Delete guard UX + ID collection for moderator availability.
 *
 * Overlap rules on the API use UTC for day_of_week + time window (same as rows saved from TeamAvailability).
 * UI may show PST (or another IANA zone) but persisted slots are UTC-consistent for blocking checks.
 */
export const MODERATOR_AVAILABILITY_DELETE_BLOCKED_DESCRIPTION =
  'There are sessions not started or in progress on this availability. Cancel or reschedule them before deleting.';

export type AvailabilityDeleteSlot = {recordId?: string | null};

/** Record IDs to pass to check-delete / DELETE, matching one grouped UI block. */
export function collectAvailabilityRecordIdsForDelete(
  moderatorId: string,
  records: any[],
  timeSlots: AvailabilityDeleteSlot[],
): string[] {
  const recordsForMod = records.filter((r) => r.moderator_id === moderatorId);
  const fromSlots = timeSlots
    .map((s) => s.recordId)
    .filter((id): id is string => id != null && String(id).trim() !== '');
  if (fromSlots.length > 0) return fromSlots.map(String);
  return recordsForMod.map((r) => r.id).filter(Boolean).map(String);
}

/** Parses check-delete API response (axios body). */
export function getBlockingSchedulesFromCheckDeleteResponse(res: unknown): unknown[] {
  const r = res as {data?: Record<string, unknown>} | undefined;
  const data = r?.data ?? {};
  const inner = data.data as Record<string, unknown> | undefined;
  const blocking =
    (data.blocking_schedules as unknown[] | undefined) ??
    (inner?.blocking_schedules as unknown[] | undefined);
  return Array.isArray(blocking) ? blocking : [];
}

/** Parses blocked availability IDs from check-delete API response (axios body). */
export function getBlockingAvailabilityIdsFromCheckDeleteResponse(
  res: unknown,
): string[] {
  const r = res as {data?: Record<string, unknown>} | undefined;
  const data = r?.data ?? {};
  const inner = data.data as Record<string, unknown> | undefined;
  const ids =
    (data.blocking_availability_ids as unknown[] | undefined) ??
    (inner?.blocking_availability_ids as unknown[] | undefined);
  if (!Array.isArray(ids)) return [];
  return ids
    .map((id) =>
      (typeof id === 'string' || typeof id === 'number' ? String(id) : '').trim(),
    )
    .filter((id) => id.length > 0);
}
